import { Link } from "react-router-dom";
import { ArrowRight, Flame, Percent, ShieldCheck, Tag } from "lucide-react";
import HeroSlider from "@/components/hero/HeroSlider";
import MobileHero from "@/components/hero/MobileHero";
import TrustStrip from "@/components/TrustStrip";
import SectionHeader from "@/components/SectionHeader";
import CategoryCards from "@/components/CategoryCards";
import { ProductCard, ProductCardSkeleton } from "@/components/ProductCard";
import ServicesSection from "@/components/ServicesSection";
import ReviewsSection from "@/components/ReviewsSection";
import FeaturedShops from "@/components/FeaturedShops";
import PaymentBadges from "@/components/PaymentBadges";
import Countdown from "@/components/Countdown";
import { useHomeData } from "@/hooks/useCatalog";
import { useStore } from "@/context/StoreContext";
import { formatPrice } from "@/lib/format";
import { useSeo, siteUrl } from "@/lib/seo";

export default function Home() {
  const { data, isLoading } = useHomeData();
  const { currency } = useStore();

  useSeo({
    title: "ISMO-TECH — Vos licences officielles au meilleur prix",
    description:
      "Achetez vos licences Windows, Microsoft Office, antivirus, Adobe, outils professionnels et services de déblocage autorisés. Livraison instantanée, paiement sécurisé, support 7j/7.",
    image: "/logo-full.png",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          "@id": `${siteUrl("/")}#organization`,
          name: "ISMO-TECH",
          url: siteUrl("/"),
          logo: siteUrl("/logo-full.png"),
          slogan: "Vos licences officielles au meilleur prix.",
        },
        {
          "@type": "WebSite",
          "@id": `${siteUrl("/")}#website`,
          name: "ISMO-TECH",
          url: siteUrl("/"),
          publisher: { "@id": `${siteUrl("/")}#organization` },
          potentialAction: {
            "@type": "SearchAction",
            target: { "@type": "EntryPoint", urlTemplate: `${siteUrl("/boutique")}?q={search_term_string}` },
            "query-input": "required name=search_term_string",
          },
        },
        {
          "@type": "OnlineStore",
          name: "ISMO-TECH",
          url: siteUrl("/"),
          paymentAccepted: "Orange Money, Wave, Moov Money, MTN Mobile Money, Visa, Mastercard",
        },
      ],
    },
  });

  const slides = data?.slides || [];
  const promo = data?.promotions?.[0];

  return (
    <div>
      <div className="hidden lg:block">
        <HeroSlider slides={slides} />
      </div>
      <div className="lg:hidden">
        <MobileHero slides={slides} />
      </div>

      <div className="hidden lg:block">
        <TrustStrip />
      </div>

      {/* ---------------------------------------------------- catégories */}
      <section className="container-it pt-8 lg:pt-20">
        <SectionHeader
          title="Catégories populaires"
          subtitle="Explorer"
          linkTo="/categories"
          linkLabel="Voir toutes les catégories"
          testId="home-categories-title"
        />
        <div className="lg:hidden">
          <CategoryCards categories={data?.categories || []} variant="mobile" />
        </div>
        <div className="hidden lg:block">
          <CategoryCards categories={(data?.categories || []).slice(0, 8)} />
        </div>
      </section>

      {/* ---------------------------------------------------- bannière promo mobile */}
      {promo && (
        <section className="container-it mt-6 lg:hidden">
          <Link
            to="/promotions"
            data-testid="mobile-promo-banner"
            className="flex items-center gap-3 rounded-2xl bg-brand p-4 text-white shadow-[0_14px_30px_-12px_rgba(0,82,255,0.8)]"
          >
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/15">
              <Percent className="h-6 w-6" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-extrabold">Offres spéciales</span>
              <span className="block truncate text-xs text-white/80">
                Jusqu'à -{promo.discount_percent}% sur une sélection de produits
              </span>
            </span>
            <ArrowRight className="h-5 w-5 shrink-0" />
          </Link>
        </section>
      )}

      {/* ---------------------------------------------------- produits populaires */}
      <section className="container-it pt-10 lg:pt-20">
        <SectionHeader
          title="Produits populaires"
          subtitle="Les plus vendus"
          linkTo="/boutique"
          linkLabel="Voir toute la boutique"
          testId="home-featured-title"
        />
        <div className="grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-4">
          {isLoading
            ? Array.from({ length: 8 }).map((_, i) => <ProductCardSkeleton key={i} />)
            : (data?.featured || []).map((product, i) => (
                <ProductCard key={product.id} product={product} index={i} />
              ))}
        </div>
      </section>

      {/* ---------------------------------------------------- offres du moment */}
      {data?.promotions?.length > 0 && (
        <section className="mt-14 bg-night py-12 lg:mt-24 lg:py-20">
          <div className="container-it">
            <div className="mb-7 flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-rose-400">
                  <Flame className="h-4 w-4" /> Offres limitées
                </p>
                <h2 className="font-display text-[22px] font-extrabold text-white sm:text-3xl">Offres du moment</h2>
              </div>
              <div className="flex items-center gap-4">
                {data.promotions.find((p) => p.promo_ends_at) && (
                  <Countdown endsAt={data.promotions.find((p) => p.promo_ends_at).promo_ends_at} dark />
                )}
                <Link
                  to="/promotions"
                  data-testid="home-promotions-link"
                  className="hidden items-center gap-1.5 rounded-full border border-white/20 px-4 py-2 text-xs font-bold text-white transition-colors duration-200 hover:bg-white/10 sm:flex"
                >
                  Voir toutes les promotions <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {data.promotions.slice(0, 6).map((product) => (
                <Link
                  key={product.id}
                  to={`/boutique/${product.slug}`}
                  data-testid={`promo-item-${product.slug}`}
                  className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-4 backdrop-blur-md transition-colors duration-300 hover:border-brand/60 hover:bg-white/[0.08]"
                >
                  <img
                    src={product.image_url}
                    alt={product.name}
                    loading="lazy"
                    className="h-20 w-20 shrink-0 rounded-xl bg-white object-contain p-1.5"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="rounded bg-rose-500 px-1.5 py-0.5 text-[10px] font-black text-white">
                        -{product.discount_percent}%
                      </span>
                      <span className="truncate text-[11px] uppercase tracking-wide text-slate-400">
                        {product.license_type}
                      </span>
                    </span>
                    <span className="mt-1.5 line-clamp-2 text-sm font-bold text-white">{product.name}</span>
                    <span className="mt-1.5 flex items-baseline gap-2">
                      <span className="font-display text-base font-extrabold text-white">
                        {formatPrice(product.price, currency)}
                      </span>
                      <span className="text-xs text-slate-500 line-through">
                        {formatPrice(product.old_price, currency)}
                      </span>
                    </span>
                  </span>
                </Link>
              ))}
            </div>

            <Link
              to="/promotions"
              className="mt-6 flex items-center justify-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-bold text-white sm:hidden"
            >
              Voir toutes les promotions <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      )}

      {/* ---------------------------------------------------- nouveautés */}
      <section className="container-it pt-14 lg:pt-20">
        <SectionHeader
          title="Nouveautés du catalogue"
          subtitle="Fraîchement ajoutés"
          linkTo="/boutique?tri=new"
          testId="home-new-title"
        />
        <div className="grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-4">
          {(data?.new_arrivals || []).slice(0, 4).map((product, i) => (
            <ProductCard key={product.id} product={product} index={i} />
          ))}
        </div>
      </section>

      {/* ---------------------------------------------------- boutiques vedettes */}
      <FeaturedShops />

      {/* ---------------------------------------------------- services */}
      <section className="container-it pt-14 lg:pt-20">
        <SectionHeader
          title="Nos services"
          subtitle="Assistance autorisée"
          linkTo="/services"
          linkLabel="Tous nos services"
          testId="home-services-title"
        />
        <ServicesSection services={(data?.services || []).slice(0, 6)} />
      </section>

      {/* ---------------------------------------------------- avis */}
      {(data?.reviews || []).length > 0 && (
        <section className="container-it pt-14 lg:pt-20">
          <SectionHeader title="Ce que pensent nos clients" subtitle="Avis vérifiés" testId="home-reviews-title" />
          <ReviewsSection reviews={data?.reviews || []} rating={data?.rating || {}} />
        </section>
      )}

      {/* ---------------------------------------------------- paiements */}
      <section className="container-it pt-14 lg:pt-20">
        <div className="rounded-2xl border border-line bg-surface p-6 sm:p-10">
          <div className="grid gap-8 lg:grid-cols-[1fr_1.2fr] lg:items-center">
            <div>
              <p className="label-eyebrow mb-2">Paiement</p>
              <h2 className="font-display text-[22px] font-extrabold text-ink sm:text-3xl">
                Payez comme vous voulez
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">
                Cartes bancaires, portefeuilles en ligne et Mobile Money : payez avec le moyen qui vous convient,
                votre commande est vérifiée et traitée par notre équipe.
              </p>
              <div className="mt-5 flex items-center gap-2 rounded-xl border border-brand/20 bg-brand-soft p-3 text-xs font-semibold text-brand">
                <ShieldCheck className="h-4 w-4" />
                Paiement 100% sécurisé : chaque transaction est vérifiée avant la livraison.
              </div>
            </div>
            <div>
              <PaymentBadges />
              <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[
                  { label: "Livraison", value: "2 min" },
                  { label: "Produits", value: "24+" },
                  { label: "Support", value: "7j/7" },
                  { label: "Note", value: `${data?.rating?.average || 4.9}/5` },
                ].map((stat) => (
                  <div key={stat.label} className="rounded-xl border border-line bg-white p-4">
                    <p className="font-display text-xl font-extrabold text-ink">{stat.value}</p>
                    <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                      {stat.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="container-it pb-4 pt-14 lg:pt-20">
        <div className="flex flex-col items-start gap-5 rounded-2xl bg-brand p-7 text-white sm:flex-row sm:items-center sm:justify-between sm:p-10">
          <div>
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-white/70">
              <Tag className="h-4 w-4" /> Code promo
            </p>
            <h2 className="mt-2 font-display text-xl font-extrabold sm:text-2xl">
              -10% sur votre première commande
            </h2>
            <p className="mt-1.5 text-sm text-white/80">
              Utilisez le code <span className="font-bold">ISMO10</span> au moment du paiement (dès 15 000 FCFA).
            </p>
          </div>
          <Link
            to="/boutique"
            data-testid="home-cta-shop"
            className="rounded-full bg-white px-7 py-3.5 text-sm font-bold text-brand transition-transform duration-200 hover:scale-105 active:scale-95"
          >
            J'en profite
          </Link>
        </div>
      </section>
    </div>
  );
}
